// BL580 — humanize CLI vocabulary.
//
// A typed catalogue mapping a structured failure `kind` to the three lines a
// user actually reads when a plug fails: a plain headline, a one-sentence
// expansion, and a one-sentence next step. The CLI relay (BL591) and the
// react-layer (BL581) render FROM this catalogue instead of leaking the
// platform's internal vocabulary (pod / image / SHA / Cloud Build / GSA / IAM)
// per backlog 580 §1 and the §23 brand voice (plain, specific, confident; no
// marketing adjectives, no emoji, no exclamation marks).
//
// ── Which `kind`? ──
// The catalogue is keyed on {@link TaskFailureReason} — the task-level failure
// shape (`build_failed`, `rollout_failed` + sub_kind, `validation_failed`,
// `provision_failed`) that the queued-plug poll reads off
// `GET /api/v2/tasks/:id`'s `error_message` and relays to the user. That is the
// single consumer named by the backlog (`packages/cli/src/services/task-poll.ts`,
// `proj.error.kind`). The entity-level {@link EntityFailureReason} carries a
// DIFFERENT, lower-level kind set (`pod_crashloop`, `image_pull_error`,
// `oom_killed`, `timeout`, `probe_failed`, `stale_image`); those are the raw
// signals the task layer CLASSIFIES into the four task kinds above. Humanizing
// happens once, at the task boundary the user sees.
//
// ── Escape hatch ──
// `humanize()` carries an optional `log_tail` — the build-log tail / rollout
// evidence already secret-scrubbed at the persistence boundary (BL591 §C). That
// is the user's OWN output (their failing build script, their crash trace), not
// platform vocabulary, so it is shown beneath the headline. The catalogue's
// own `title` / `detail` / `next_step` strings stay free of leaks; the §23 +
// no-leak gate in `__tests__/cli-vocab.test.ts` enforces that.

import type { TaskFailureReason } from './v1-types.js';

/** The three sub-kinds a `rollout_failed` can narrow to. */
export type RolloutSubKind = 'pod_crashloop' | 'probe_race' | 'image_pin_miss';

/** One catalogue row: the three lines a user reads for a given failure kind. */
export interface VocabEntry {
  kind: TaskFailureReason['kind'];
  /** One-line headline. Plain and specific — "Build failed", not "Uh oh". */
  title: string;
  /** One-sentence expansion of what went wrong, in product words. */
  detail: string;
  /** One-sentence pointer to the single command that moves the user forward. */
  next_step: string;
}

/** The shape `humanize()` returns: the three lines plus the optional raw tail. */
export interface HumanizedFailure {
  title: string;
  detail: string;
  next_step: string;
  /** The user's own scrubbed build/runtime output, shown beneath. Optional. */
  log_tail?: string;
}

/**
 * The base catalogue — one row per `TaskFailureReason` kind. Because this is a
 * `Record` over the union, adding a new kind to `TaskFailureReason` without a
 * row here is a COMPILE error: the catalogue can never silently miss a kind.
 *
 * `rollout_failed`'s row is the generic fallback; the sub-kind specialisations
 * live in {@link ROLLOUT_VOCAB} and override `detail` / `next_step` when the
 * sub-kind is known.
 */
export const VOCAB: Record<TaskFailureReason['kind'], VocabEntry> = {
  build_failed: {
    kind: 'build_failed',
    title: 'Build failed',
    detail: 'Your build ran but exited with an error before your code could go live.',
    next_step: 'Open the build log for the failing step, fix the error, then plug again.',
  },
  rollout_failed: {
    kind: 'rollout_failed',
    title: "Couldn't bring your app online",
    detail: 'Your build finished, but the new version never came up cleanly.',
    next_step: 'Run `cloudgrid logs <entity>` to see the startup output, then plug again.',
  },
  validation_failed: {
    kind: 'validation_failed',
    title: 'Your cloudgrid.yaml has a problem',
    detail: "The platform read your cloudgrid.yaml and couldn't deploy it as written.",
    next_step: 'Fix the problem in cloudgrid.yaml, then plug again.',
  },
  provision_failed: {
    kind: 'provision_failed',
    title: "Couldn't set up your environment",
    detail: "Your account setup didn't finish, so there's nowhere to run your app yet.",
    next_step: 'Try again in a moment. If it keeps failing, contact support.',
  },
};

/**
 * Sub-kind specialisations for `rollout_failed`. Each narrows the generic
 * `VOCAB.rollout_failed` row to a sharper `detail` + `next_step`; the `title`
 * stays the same headline. Keyed as a `Record` over {@link RolloutSubKind} so a
 * new sub-kind is a compile error here too.
 */
export const ROLLOUT_VOCAB: Record<RolloutSubKind, Pick<VocabEntry, 'detail' | 'next_step'>> = {
  pod_crashloop: {
    detail: 'Your app crashed on startup.',
    next_step: 'Run `cloudgrid logs <entity>` to see the crash output, fix it, then plug again.',
  },
  probe_race: {
    detail: "Your app started but didn't answer a health check in time.",
    next_step:
      'Run `cloudgrid status <entity>` in a moment — rollouts usually finish within a minute. If it stays down, run `cloudgrid logs <entity>`.',
  },
  image_pin_miss: {
    detail: "Your new build didn't replace the running version.",
    next_step:
      'Run `cloudgrid status <entity>` in a moment. If it still shows the previous version, plug again.',
  },
};

/**
 * Render a structured task failure into the three user-facing lines, picking the
 * `rollout_failed` sub-kind specialisation when present and carrying the
 * (already-scrubbed) raw tail through as `log_tail` for the deep-dive line.
 *
 * `validation_failed` is the one kind whose `detail` comes from the failure
 * itself: the message there IS the user's own cloudgrid.yaml problem (their
 * word, not the platform's), so surfacing it is correct. Every other kind uses
 * static, leak-free catalogue copy and keeps the technical specifics in
 * `log_tail`.
 */
export function humanize(failure: TaskFailureReason): HumanizedFailure {
  switch (failure.kind) {
    case 'build_failed': {
      const base = VOCAB.build_failed;
      return {
        title: base.title,
        detail: base.detail,
        next_step: base.next_step,
        ...(failure.log_tail ? { log_tail: failure.log_tail } : {}),
      };
    }
    case 'rollout_failed': {
      const base = VOCAB.rollout_failed;
      const sub = ROLLOUT_VOCAB[failure.sub_kind];
      return {
        title: base.title,
        detail: sub?.detail ?? base.detail,
        next_step: sub?.next_step ?? base.next_step,
        ...(failure.evidence ? { log_tail: failure.evidence } : {}),
      };
    }
    case 'validation_failed': {
      const base = VOCAB.validation_failed;
      return {
        title: base.title,
        // The validation message is the user's own yaml problem — show it.
        detail: failure.message?.length ? failure.message : base.detail,
        next_step: base.next_step,
      };
    }
    case 'provision_failed': {
      const base = VOCAB.provision_failed;
      return { title: base.title, detail: base.detail, next_step: base.next_step };
    }
  }
}
